/** Preferencias de UI que deben conservarse tras logout (no incluir en clear global). */
export const VAULT_PERSISTENT_LOCAL_STORAGE_KEYS = [
  'vault_admin_hidden_user_ids',
  'vault_admin_hidden_user_emails',
] as const;

/** Claves de sesión Vault/Simappe que deben limpiarse al logout o antes de un nuevo login. */
export const VAULT_SESSION_STORAGE_KEYS = [
  'access_token',
  'refresh_token',
  'token_expiry',
  'token_timestamp',
  'user_data',
  'vault_operative_role',
  'vault_is_superadmin_scope',
  'vault_dashboard_role',
  /** Pin de rol/tenant: si sobrevive al logout, otro usuario hereda admin/superadmin en Bóveda. */
  'vault_session_pin_v1',
  'vault_operative_role_pinned',
  'vault_tenant_snapshot',
  'vault_user_tenant_assignments',
  'vault_tenant_catalog_ls',
  'tenant_id',
  'tenantId',
  'selected_tenant_id',
  'vault_server_context',
  'hasMultipleModules',
  'selectedProductId',
  'selectedRoute',
  'selectedModule',
] as const;

export const VAULT_SESSION_SESSION_KEYS = [
  'vault_auth_me_tenants',
  'vault_tenant_catalog',
  'vault_nebula_company',
  'hasMultipleModules',
  'selectedProductId',
  'selectedRoute',
  'selectedModule',
  'session_expired_shown',
  'refresh_count',
] as const;

/** Se conserva durante clearVaultSessionStorage hasta el próximo login en Vault. */
export const VAULT_POST_LOGIN_SESSION_KEYS = [
  'vault_post_login_return_url',
  'vault_logout_reason',
  'vault_tabs_snapshot_v1',
  'vault_login_blocked_message',
  'vault_account_blocked_circuit',
] as const;

export const VAULT_SESSION_COOKIE_NAMES = ['app_data', 'user_profile', 'session_data'] as const;

export const VAULT_NAV_STORAGE_KEY = 'nebula_ui_nav_v1';

const ROUTE_STORAGE_PREFIX = 'nebula:activeRoute:';
/** Módulos con ruta activa persistida (evitar nav cruzada entre productos). */
const VAULT_LAYOUT_ROUTE_MODULES = ['accounting', 'tesorería', 'budget', 'vault', 'maestros'] as const;

/**
 * Claves extra de navegación (producto/ruta seleccionada).
 * No incluye `vault_tabs_snapshot_v1` ni otras claves de restore por expiración
 * (`VAULT_POST_LOGIN_SESSION_KEYS`): esas se conservan hasta re-login.
 */
const VAULT_NAV_EXTRA_KEYS = ['selectedRoute', 'lastUsedProductId', 'selectedProductId'] as const;

export function clearVaultNavigationCache(): void {
  if (typeof window === 'undefined') return;

  localStorage.removeItem(VAULT_NAV_STORAGE_KEY);
  for (const module of VAULT_LAYOUT_ROUTE_MODULES) {
    localStorage.removeItem(`${ROUTE_STORAGE_PREFIX}${module}`);
  }
  for (const key of VAULT_NAV_EXTRA_KEYS) {
    localStorage.removeItem(key);
  }
}

export function clearVaultSessionStorage(): void {
  if (typeof window === 'undefined') return;

  for (const key of VAULT_SESSION_STORAGE_KEYS) {
    localStorage.removeItem(key);
  }

  for (const key of VAULT_SESSION_SESSION_KEYS) {
    sessionStorage.removeItem(key);
  }

  // Bóveda Digital: no heredar currentPath entre usuarios/roles (#0000143 C2).
  clearVaultRepositoryAdminSessionState();

  // Defensa: pin legacy / claves añadidas fuera de la lista fija.
  try {
    localStorage.removeItem('vault_session_pin_v1');
    localStorage.removeItem('vault_operative_role_pinned');
  } catch {
    /* ignore */
  }

  // vault_post_login_return_url y vault_logout_reason se conservan hasta el re-login en Vault.
}

/** Elimina estados persistidos del explorador de Bóveda (todas las claves por tenant/usuario). */
export function clearVaultRepositoryAdminSessionState(): void {
  if (typeof window === 'undefined') return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('vault_repository_admin_state')) {
        keys.push(key);
      }
    }
    for (const key of keys) {
      sessionStorage.removeItem(key);
    }
  } catch {
    /* ignore */
  }
}

export function clearVaultSessionCookies(cookieService?: {
  delete: (name: string, path: string, domain?: string) => void;
}): void {
  if (typeof window === 'undefined') return;

  const hostname = window.location.hostname;

  for (const name of VAULT_SESSION_COOKIE_NAMES) {
    if (cookieService) {
      cookieService.delete(name, '/');
      cookieService.delete(name, '/', hostname);
      cookieService.delete(name, '/', 'localhost');
    }

    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    if (hostname) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${hostname}`;
    }
  }
}
